import { motion } from 'framer-motion';
 
const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
    },
};
 
interface PolicySection {
    heading?: string;
    paragraphs?: string[];
    bullets?: string[];
    subSections?: { label: string; bullets: string[] }[];
}
 
const sections: PolicySection[] = [
    {
        paragraphs: [
            'JAPFA COMFEED INDIA PVT. LTD. ("JAPFA", "Company", "we", "our", or "us") is committed to protecting the privacy and security of information collected through the JAPFA Intranet Portal, including its mobile application and web portal (collectively referred to as the "Platform").',
            'This Privacy Policy describes how we collect, use, store, process, and protect information when employees and authorized users access or use the Platform for internal business, administrative, and self-service activities.',
            'By using the Platform, you agree to the collection and use of information in accordance with this Privacy Policy.',
        ],
    },
    {
        heading: '2. Purpose of the Application',
        paragraphs: [
            'The JAPFA Intranet Portal is designed to support internal employee and business operations including dashboards and reporting, requests and approvals, document and policy access, vendor and procurement workflows, budget and finance tracking, and other administrative activities within the Company.',
            'The application is intended solely for authorized business and operational purposes related to employee productivity, internal communication, and organizational compliance.',
        ],
    },
    {
        heading: '3. Information We Collect',
        paragraphs: [
            'Depending on your role and usage of the Platform, we may collect the following information:',
        ],
        subSections: [
            {
                label: 'A. Personal Information',
                bullets: [
                    'Full Name',
                    'Employee ID',
                    'Mobile Number',
                    'Email Address',
                    'Department or Business Unit',
                    'Designation and Reporting Manager',
                    'User Role and Access Permissions',
                ],
            },
            {
                label: 'B. Request and Approval Information',
                bullets: [
                    'Requests raised, assigned, and their approval status',
                    'Request History and Audit Trail',
                    'Asset Related Information',
                    'Complaints, Hazard, and Bio-Security Records (where applicable to your role)',
                ],
            },
            {
                label: 'C. Document and Policy Information',
                bullets: [
                    'HR, EHS, IT, and Legal Policy Acknowledgements',
                    'Uploaded Documents, Attachments, and Supporting Evidence',
                    'Signature Records (where applicable)',
                ],
            },
            {
                label: 'D. Dashboard and Reporting Information',
                bullets: [
                    'Access to Power BI, NSM, Feed Sales, and Management Dashboards',
                    'Traffic Light and Compliance Status Records',
                    'SAP Auth and Master Data linked to your role',
                ],
            },
            {
                label: 'E. Device and Technical Information',
                bullets: [
                    'Device Model',
                    'Device Operating System',
                    'Application Version',
                    'IP Address',
                    'Browser Information',
                    'Login Activity',
                    'System Logs',
                    'Error Reports',
                ],
            },
        ],
    },
    {
        heading: '4. Authentication and Access Control',
        paragraphs: [
            'Access to the Platform is granted through authorized company credentials and role-based permissions.',
        ],
        bullets: [
            'Verifying your identity through Single Sign-On or company login credentials',
            'Restricting access to modules and data based on your assigned role',
            'Maintaining audit logs of login and access activity',
            'Enforcing session security, including OTP verification and password resets where applicable',
        ],
    },
    {
        heading: '5. Camera and File Access',
        paragraphs: [
            'The Platform may request access to the device camera or file storage to:',
        ],
        bullets: [
            'Capture or upload photographs and documents supporting a request',
            'Attach evidence for approvals, audits, or complaints',
            'Upload signatures or identity documents where required',
            'Support verification and investigation processes',
        ],
    },
    {
        heading: '6. Storage Access',
        paragraphs: ['The Platform may request access to device storage for:'],
        bullets: [
            'Uploading documents, attachments, and evidence related to requests and approvals',
            'Downloading reports, policies, and reference materials',
            'Storing application-related files required for portal operations',
        ],
    },
    {
        heading: '7. How We Use Information',
        paragraphs: [
            'Information collected through the Platform may be used to:',
        ],
        bullets: [
            'Process, assign, and track requests and approvals across modules',
            'Provide dashboards, analytics, and management reporting',
            'Maintain masters and role-based access across the organization',
            'Track budgets, procurement, and finance provisions',
            'Distribute and record acknowledgement of company policies',
            'Maintain system security and prevent unauthorized access',
            'Provide technical support',
            'Comply with applicable legal and regulatory requirements',
        ],
    },
    {
        heading: '8. Data Sharing and Disclosure',
        paragraphs: [
            'We do not sell personal information.',
            'Information may be shared only under the following circumstances:',
        ],
        subSections: [
            {
                label: 'Authorized Internal Users',
                bullets: [
                    'Information may be accessed by authorized employees, approvers, department heads, administrators, and management personnel who require access for legitimate business purposes.',
                ],
            },
            {
                label: 'Service Providers',
                bullets: [
                    'Cloud hosting',
                    'Technical support',
                    'Application maintenance',
                    'Data storage',
                    'System monitoring',
                ],
            },
            {
                label: 'Legal Requirements',
                bullets: [
                    'Applicable laws',
                    'Government and regulatory authorities',
                    'Court orders',
                    'Regulatory requirements',
                    'Law enforcement agencies',
                ],
            },
        ],
    },
    {
        heading: '9. Data Retention',
        paragraphs: ['We retain information only for as long as necessary to:'],
        bullets: [
            'Support ongoing business and administrative operations',
            'Maintain statutory and compliance records',
            'Fulfill legal and regulatory obligations',
            'Conduct audits and investigations',
            'Resolve disputes or claims',
            'Enforce company policies',
        ],
    },
    {
        heading: '10. Data Security',
        paragraphs: [
            'We implement reasonable administrative, technical, and organizational safeguards to protect information against unauthorized access, disclosure, alteration, or destruction.',
            'Security measures may include:',
        ],
        bullets: [
            'Secure authentication mechanisms, including OTP and role-based login',
            'Role-based access control',
            'Password protection',
            'Encrypted communications',
            'Audit logs and activity monitoring',
            'Periodic security reviews',
        ],
    },
    {
        heading: '11. User Responsibilities',
        paragraphs: ['Users are responsible for:'],
        bullets: [
            'Maintaining confidentiality of login credentials',
            'Protecting authorized devices',
            'Providing accurate information when raising requests or approvals',
            'Reporting unauthorized access or suspected security incidents',
            'Using the Platform only for authorized business activities',
        ],
    },
    {
        heading: "12. Children's Privacy",
        paragraphs: [
            'The Platform is intended solely for authorized business users and is not directed toward individuals under the age of 18 years.',
            'We do not knowingly collect personal information from children.',
        ],
    },
    {
        heading: '13. Your Rights',
        paragraphs: ['Subject to applicable laws, users may request:'],
        bullets: [
            'Access to their personal information',
            'Correction of inaccurate information',
            'Deletion of information where legally permissible',
            'Restriction of processing under certain circumstances',
        ],
    },
    {
        heading: '14. Account Deletion Request',
        paragraphs: [
            'Users may request deletion of their account and associated personal information by contacting the Intranet Support Team.',
            'Certain request, approval, financial, legal, or compliance records may be retained where required by applicable laws or legitimate business requirements.',
            'For account deletion requests, contact: buddyassist.jcipl@japfa.com',
        ],
    },
    {
        heading: '15. Changes to this Privacy Policy',
        paragraphs: [
            'We may update this Privacy Policy from time to time to reflect operational, legal, regulatory, or technological changes.',
            "The latest version will always be available through the Platform and on the Company's website.",
            'Continued use of the Platform after any updates constitutes acceptance of the revised Privacy Policy.',
        ],
    },
    {
        heading: '16. Contact Us',
        paragraphs: [
            'If you have questions regarding this Privacy Policy or the handling of your information, please contact:',
            'JAPFA COMFEED INDIA PVT. LTD.\nPlot No. 91, Sakore Nagar\nViman Nagar\nPune – 411014\nIndia',
            'Email: buddyassist.jcipl@japfa.com',
            'Website: https://www.japfaindia.com',
        ],
    },
];
 
const IntranetPortalPolicy = () => {
    return (
        <>
            <section className="flex justify-center bg-[#160700] py-[60px] ps-8 align-middle lg:ps-14 xl:py-[80px]">
                <motion.div
                    className="my-auto flex flex-col items-start justify-center sm:w-[60%] md:w-[60%]"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                >
                    <h1 className="text-[28px] font-bold leading-[120%] text-white lg:text-[40px] xl:text-[52px]">
                        Privacy Policy – JAPFA Intranet Portal
                    </h1>
                </motion.div>
            </section>
 
            {/* Policy Body */}
            <section className="bg-white px-6 py-12 sm:px-10 md:px-16 lg:px-24 xl:px-32">
                <div className="">
                    {/* Introduction paragraph before numbered sections */}
                    <motion.div
                        className="mb-6"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                    >
                        <h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
                            1. Introduction
                        </h2>
                        {sections[0].paragraphs?.map((p, i) => (
                            <p
                                key={i}
                                className="mb-3 text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
                            >
                                {p}
                            </p>
                        ))}
                    </motion.div>
 
                    {/* Remaining sections */}
                    {sections.slice(1).map((section, sIdx) => (
                        <motion.div
                            key={sIdx}
                            className="mb-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.1 }}
                            variants={fadeUp}
                        >
                            {section.heading && (
                                <h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
                                    {section.heading}
                                </h2>
                            )}
 
                            {section.paragraphs?.map((p, i) => (
                                <p
                                    key={i}
                                    className="mb-3 whitespace-pre-line text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
                                >
                                    {p}
                                </p>
                            ))}
 
                            {section.bullets && section.bullets.length > 0 && (
                                <ul className="mb-3 ml-5 list-disc space-y-1">
                                    {section.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="text-[15px] leading-[1.75] text-[#1b1b1b]"
                                        >
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            )}
 
                            {section.subSections?.map((sub, subIdx) => (
                                <div key={subIdx} className="mb-4 ml-2">
                                    <p className="mb-1 text-[15px] font-semibold text-[#1b1b1b]">
                                        {sub.label}
                                    </p>
                                    <ul className="ml-5 list-disc space-y-1">
                                        {sub.bullets.map((b, i) => (
                                            <li
                                                key={i}
                                                className="text-[15px] leading-[1.75] text-[#1b1b1b]"
                                            >
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </motion.div>
                    ))}
                </div>
            </section>
        </>
    );
};
 
export default IntranetPortalPolicy;
